/* eslint-disable react/no-unused-prop-types */
import { Box } from '@mui/material';
import UPButton from 'components/button';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import { UPDropdown } from 'components/dropdown';
import { RowSeverity, UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { CompanyRole } from 'constants/CompanyRole';
import { toNumber } from 'lodash';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getParitairComiteSelectItemsByCompanyAction } from 'store/actions/paritair-comite-actions';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { useGridPagination } from 'hooks/useGridPagination';
import { useGridSearch } from 'hooks/useGridSearch';
import IEmployeeExemption from 'types/company/payroll/IEmployeeExemption';
import { activeEmployeeDropdownSelector } from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import { paritairComiteByCompanySelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import {
  COMPANY_PAYROLL__ADD_NEW_EMPLOYEE_EXEMPTION,
  COMPANY_PAYROLL__EMPLOYEE,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  COMPANY_PAYROLL__WAGE_EXEMPTION,
  DATAGRID__NO_ROWS,
  GENERAL__SELECT,
  WAGE_INDEXATION__VALID_FROM,
  WAGE_INDEXATION__VALID_TO,
} from 'translations/constants';
import { EmployeesExemptionsGridProps, WageExemptionField } from '../types';
import { DeleteActionCell, WageInputCell } from './cells';
import { isUnsavedWageExemptionRow } from './helpers';

const EmployeeExemptionsGrid = (props: EmployeesExemptionsGridProps) => {
  const { employeesExemptions, setEmployeesExemptions, rowErrors } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const employees = useAppSelector(activeEmployeeDropdownSelector);
  const userIsCompanyManager = useAppSelector(profileSelector).role === CompanyRole.COMPANY_MANAGER;
  const paritairComites = useAppSelector(paritairComiteByCompanySelectItemsSelector);

  // Used directly rather than copied into state: the previous copy latched onto the first non-empty list and
  // then ignored every later update, so the three grids now all read their options straight from the source.
  const employeesOptions = employees;

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsByCompanyAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const matches = useCallback(
    (row: IEmployeeExemption, term: string) => {
      const needle = term.toLowerCase();
      const employeeLabel =
        employeesOptions.find(o => toNumber(o.value) === row.employeeId || o.value === row.employeeId?.toString())
          ?.label ?? '';
      const paritairLabel = paritairComites?.find(p => p.value === row.paritairComiteId)?.label ?? '';
      const startDate = (row.startDate ?? '').toString();
      const endDate = (row.endDate ?? '').toString();
      return (
        employeeLabel.toLowerCase().includes(needle) ||
        paritairLabel.toLowerCase().includes(needle) ||
        (row.wageExemption?.toString() ?? '').toLowerCase().includes(needle) ||
        startDate.toLowerCase().includes(needle) ||
        endDate.toLowerCase().includes(needle)
      );
    },
    [employeesOptions, paritairComites],
  );

  const isUnsavedRow = useCallback((row: IEmployeeExemption) => isUnsavedWageExemptionRow(row), []);
  const { onSearch, filteredRows } = useGridSearch(employeesExemptions, matches, isUnsavedRow);
  const getRowId = useCallback((row: IEmployeeExemption) => row.id, []);
  const { pagination, setPagination, revealRow, resetToFirstPage, scrollToRowId } = useGridPagination(
    filteredRows,
    getRowId,
  );

  // A narrower term can leave fewer pages than the one being viewed, and the first match is the one worth
  // showing anyway.
  const onSearchChange = useCallback(
    (value: string) => {
      onSearch(value);
      resetToFirstPage();
    },
    [onSearch, resetToFirstPage],
  );

  const updateEmployeeExemption = useCallback(
    (id, field, value) => {
      setEmployeesExemptions(prev => {
        const index = prev.findIndex(w => w.id === id);
        if (index < 0) return prev;
        const updated = { ...prev[index], [field]: value };
        return [...prev.slice(0, index), updated, ...prev.slice(index + 1)];
      });
    },
    [setEmployeesExemptions],
  );

  const onWageChange = useCallback(
    (id: number, value: number) => updateEmployeeExemption(id, 'wageExemption', value),
    [updateEmployeeExemption],
  );

  const onAddNewEmployeeWageExemption = useCallback(() => {
    const id = Math.random();
    setEmployeesExemptions(prev => [{ id, employeeId: 0, wageExemption: 0, paritairComiteId: '' }, ...prev]);
    revealRow(id);
  }, [setEmployeesExemptions, revealRow]);

  const onDeleteRow = useCallback(
    (id: number) => {
      setEmployeesExemptions(prev => prev.filter(c => c.id !== id));
    },
    [setEmployeesExemptions],
  );

  const getRowSeverity = useCallback(
    (row: IEmployeeExemption): RowSeverity | undefined => (rowErrors.rowIds.includes(row.id) ? 'error' : undefined),
    [rowErrors],
  );

  const hasFieldError = useCallback(
    (row: IEmployeeExemption, field: WageExemptionField) =>
      rowErrors.fields.includes(field) && rowErrors.rowIds.includes(row.id),
    [rowErrors],
  );

  const employeeCellRenderer = useCallback(
    ({ row }: { row: IEmployeeExemption }) => (
      <UPDropdown
        label=""
        value={(row.employeeId || '') as unknown as string}
        items={employeesOptions}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'key')}
        onChange={e => updateEmployeeExemption(row.id, 'employeeId', toNumber(e.target.value))}
      />
    ),
    [employeesOptions, t, userIsCompanyManager, updateEmployeeExemption, hasFieldError],
  );

  const paritairCellRenderer = useCallback(
    ({ row }: { row: IEmployeeExemption }) => (
      <UPDropdown
        label=""
        value={row.paritairComiteId ?? ''}
        items={paritairComites ?? []}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'jointCommission')}
        onChange={e => updateEmployeeExemption(row.id, 'paritairComiteId', e.target.value)}
      />
    ),
    [paritairComites, t, userIsCompanyManager, updateEmployeeExemption, hasFieldError],
  );

  const wageCellRenderer = useCallback(
    ({ row }: { row: IEmployeeExemption }) => (
      <WageInputCell
        id={row.id}
        value={row.wageExemption}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'wage')}
        onChange={onWageChange}
      />
    ),
    [userIsCompanyManager, onWageChange, hasFieldError],
  );

  const deleteCellRenderer = useCallback(
    ({ row }: { row: IEmployeeExemption }) => (
      <DeleteActionCell id={row.id} disabled={userIsCompanyManager} onDelete={onDeleteRow} />
    ),
    [userIsCompanyManager, onDeleteRow],
  );

  const columns = useMemo<UPTanStackTableColumn<IEmployeeExemption>[]>(
    () => [
      { id: 'employeeId', header: t(COMPANY_PAYROLL__EMPLOYEE), size: 220, cell: employeeCellRenderer },
      { id: 'paritairComiteId', header: t(COMPANY_PAYROLL__PARITAIR_COMITE), size: 220, cell: paritairCellRenderer },
      { id: 'wageExemption', header: t(COMPANY_PAYROLL__WAGE_EXEMPTION), size: 160, cell: wageCellRenderer },
      { id: 'startDate', header: t(WAGE_INDEXATION__VALID_FROM), accessorKey: 'startDate', size: 140 },
      { id: 'endDate', header: t(WAGE_INDEXATION__VALID_TO), accessorKey: 'endDate', size: 140 },
      { id: 'actions', header: '', size: 60, align: 'center', headerAlign: 'center', cell: deleteCellRenderer },
    ],
    [t, employeeCellRenderer, paritairCellRenderer, wageCellRenderer, deleteCellRenderer],
  );

  return (
    <Box>
      <Box
        sx={{
          marginTop: '32px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          <UPButton
            text={`+ ${t(COMPANY_PAYROLL__ADD_NEW_EMPLOYEE_EXEMPTION)}`}
            onClick={onAddNewEmployeeWageExemption}
            disabled={userIsCompanyManager}
          />
          <Box display="flex" alignItems="center">
            <CustomSearchBox onSearch={onSearchChange} />
          </Box>
        </Box>
      </Box>
      <UPTanStackTable
        data={filteredRows}
        columns={columns}
        getRowId={getRowId}
        getRowSeverity={getRowSeverity}
        enableSorting={false}
        pagination={pagination}
        onPaginationChange={setPagination}
        autoResetPageIndex={false}
        scrollToRowId={scrollToRowId}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        sx={{ height: 'auto', '& .MuiTableContainer-root': { maxHeight: '500px' } }}
      />
    </Box>
  );
};
export default EmployeeExemptionsGrid;
